import { useRouter } from 'next/router';
import { TabsContent } from '@radix-ui/react-tabs';
import Router from 'next/router';
import Link from 'next/link';
import Layout from '@/components/layout/Layout';
import { sanitizeRegion } from '@/utils/explorer';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/shadcn/breadcrumb';
import { Tabs, TabsList, TabsTrigger } from '@/components/shadcn/tabs';
import Regions from '@/components/ui/regions/Regions';
import useExplorerData from '@/hooks/useExplorerData';
import RegionDetails from '@/components/ui/regions/RegionDetails';
import { RegionMapWrapper } from '@/components/ui/regions/RegionMapWrapper';
import RegionMetrics from '@/components/ui/regions/RegionMetrics';

const VALID_SECTIONS = ['metrics', 'map'];

export default function Index() {
  const router = useRouter();
  const { sections = [] } = router.query;
  const [regionName = '', section = ''] = sections;

  const { data } = useExplorerData({
    processRegions: true,
  });
  const regions = data?.regions || [];
  const stats = data?.stats;
  const region = data?.regions?.find(
    region => sanitizeRegion(region.name) === sanitizeRegion(regionName),
  );

  const defaultSection = VALID_SECTIONS.includes(section) ? section : 'metrics';

  const handleTabChange = (value: string) => {
    Router.push(`/network/regions/${regionName}/${value}`, undefined, {
      shallow: true,
    });
  };

  const { description, roadLengthByTypeKm, airportData, bridgeData } =
    region?.metadata ?? {};
  const shouldRenderNewRegionPage =
    description != null &&
    roadLengthByTypeKm != null &&
    (airportData != null || bridgeData != null);

  return region?.name ? (
    <Layout gradientBg containerClassName="px-3 md:px-0">
      <div className="flex w-full justify-between">
        <Tabs
          value={defaultSection}
          className="flex size-full flex-col items-center gap-2 md:gap-6">
          <div className="flex w-full max-w-2xl flex-col items-start gap-2 md:flex-row md:items-center md:justify-between">
            <Breadcrumb className="flex h-6 items-center md:h-9">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link href="">Network</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link href="/network/regions">Regions</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{region.name}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            <TabsList className="bg-neutral-900/40">
              <TabsTrigger
                value="metrics"
                onClick={() => handleTabChange('metrics')}>
                Metrics
              </TabsTrigger>
              <TabsTrigger value="map" onClick={() => handleTabChange('map')}>
                Map
              </TabsTrigger>
            </TabsList>
          </div>
          <TabsContent
            value="metrics"
            className="flex w-full flex-col items-center">
            {shouldRenderNewRegionPage ? (
              <RegionDetails region={region} regions={regions} />
            ) : (
              <RegionMetrics region={region} />
            )}
          </TabsContent>
          <TabsContent value="map">
            <RegionMapWrapper region={region} regions={regions} />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  ) : (
    <Layout
      gradientBg
      className="md:h-screen"
      containerClassName="px-3 md:px-0">
      <Regions regions={regions} stats={stats} />
    </Layout>
  );
}
